import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Badge } from '@repletoryreach/sdk-ui';
import { EntityCardGrid } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { templates } from '@repletoryreach/sdk-api';
import { formatDateTime } from '../../../lib/format';
import { TemplateStatusBadge } from './TemplateStatusBadge';
import { useT } from '../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templates.list>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

export function TemplatesGrid({ records }: { records: Template[] }) {
  const navigate = useNavigate();
  const t = useT();
  return (
    <EntityCardGrid<Template>
      records={records}
      idAccessor="id"
      cols={3}
      spacing="md"
      onCardClick={(row) => navigate(`/templates/${row.id}`)}
      renderCard={(tpl) => (
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Text role="subtitle" fw={600} truncate>{tpl.code}</Text>
              <Text role="caption" c={MUTED}>
                {tpl.category} · {tpl.language}
              </Text>
            </Stack>
            <TemplateStatusBadge status={tpl.status} devFallback={tpl.dev_fallback} metaStatus={tpl.meta_status} />
          </Group>
          {/* Both dates always render, with a dash where there is none: a card
              that drops a row is a card of a different height, and a grid of
              those does not line up. */}
          <Group gap="md" style={{ paddingTop: 12, borderTop: '1px solid var(--rr-color-border)' }}>
            <Stat label={t('status.submitted')} value={tpl.submitted_at ? formatDateTime(tpl.submitted_at) : t('common.notAvailable')} />
            <Stat label={t('status.approved')} value={tpl.approved_at ? formatDateTime(tpl.approved_at) : t('common.notAvailable')} />
          </Group>
          {tpl.rejection_reason && (
            <Group gap="sm" align="baseline" wrap="nowrap">
              <Badge size="xs" color="red" variant="light">{t('status.rejected')}</Badge>
              <Tooltip label={tpl.rejection_reason} position="top" openDelay={200}>
                <Text role="caption" c={MUTED} truncate>{tpl.rejection_reason}</Text>
              </Tooltip>
            </Group>
          )}
        </Stack>
      )}
    />
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <Text role="caption" c={MUTED}>{label}</Text>
      <Text role="bodySmall" fw={600}>{value}</Text>
    </Stack>
  );
}
